// ============================================================================
// Blackout Service
// Manages site-level and store-level blackout periods
// ============================================================================

import {
  SiteBlackout,
  SiteBlackoutCreate,
  SiteBlackoutUpdate,
  StoreBlackout,
  StoreBlackoutCreate,
  StoreBlackoutUpdate,
} from '../types/site';
import { fetchWithAuth } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

// ============================================================================
// Site Blackouts
// ============================================================================

export async function getSiteBlackouts(siteId: number, activeOnly?: boolean): Promise<SiteBlackout[]> {
  const params = new URLSearchParams({ site_id: siteId.toString() });
  if (activeOnly !== undefined) params.append('active_only', activeOnly.toString());
  return fetchWithAuth(`${API_BASE_URL}/blackouts/site-blackouts/?${params}`);
}

export async function getAllActiveSiteBlackouts(): Promise<SiteBlackout[]> {
  return fetchWithAuth(`${API_BASE_URL}/blackouts/site-blackouts/?active_only=true`);
}

export async function createSiteBlackout(data: SiteBlackoutCreate): Promise<SiteBlackout> {
  return fetchWithAuth(`${API_BASE_URL}/blackouts/site-blackouts/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSiteBlackout(blackoutId: number, data: SiteBlackoutUpdate): Promise<SiteBlackout> {
  return fetchWithAuth(`${API_BASE_URL}/blackouts/site-blackouts/${blackoutId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSiteBlackout(blackoutId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/blackouts/site-blackouts/${blackoutId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Store Blackouts
// ============================================================================

export async function getStoreBlackouts(storeId?: number, activeOnly?: boolean): Promise<StoreBlackout[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  if (activeOnly !== undefined) params.append('active_only', activeOnly.toString());
  const queryString = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/blackouts/store-blackouts/${queryString ? '?' + queryString : ''}`);
}

export async function createStoreBlackout(data: StoreBlackoutCreate, storeId?: number): Promise<StoreBlackout> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  const queryString = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/blackouts/store-blackouts/${queryString ? '?' + queryString : ''}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateStoreBlackout(blackoutId: number, data: StoreBlackoutUpdate): Promise<StoreBlackout> {
  return fetchWithAuth(`${API_BASE_URL}/blackouts/store-blackouts/${blackoutId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteStoreBlackout(blackoutId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/blackouts/store-blackouts/${blackoutId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Named Export Object
// ============================================================================

export const blackoutService = {
  getSiteBlackouts,
  getAllActiveSiteBlackouts,
  createSiteBlackout,
  updateSiteBlackout,
  deleteSiteBlackout,
  getStoreBlackouts,
  createStoreBlackout,
  updateStoreBlackout,
  deleteStoreBlackout,
};

export default blackoutService;
